"use client";

import { useState } from "react";
import { z } from "zod";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Input, Textarea, Label } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { FormSuccessState, MotionButton } from "@/components/motion";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { api } from "@/lib/api-client";
import type { ContactPayload } from "@/lib/api-types";
import { formatNameInput, isValidPersonName } from "@/lib/validators/name";
import { cn } from "@/lib/utils";

interface ContactFormProps {
  source?: ContactPayload["source"];
  variant?: "default" | "premium";
}

type FieldErrors = {
  name?: string;
  email?: string;
  message?: string;
};

function validateContact(form: {
  name: string;
  email: string;
  message: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!isValidPersonName(form.name)) {
    errors.name = "Name must contain only letters (min 2 characters)";
  }

  const emailResult = z.string().email().safeParse(form.email.trim());
  if (!emailResult.success) {
    errors.email = "Enter a valid email address";
  }

  if (form.message.trim().length < 10) {
    errors.message = "Message must be at least 10 characters";
  }

  return errors;
}

function MotionField({
  children,
  className,
  premium = false,
}: {
  children: React.ReactNode;
  className?: string;
  premium?: boolean;
}) {
  return (
    <motion.div
      className={cn(
        premium
          ? "transition-transform focus-within:-translate-y-px"
          : "rounded-xl transition-shadow focus-within:-translate-y-px focus-within:shadow-md focus-within:shadow-primary/10",
        className
      )}
    >
      {children}
    </motion.div>
  );
}

function PremiumLabel({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="contact-label">
      {children}
    </label>
  );
}

export function ContactForm({
  source = "contact",
  variant = "default",
}: ContactFormProps) {
  const premium = variant === "premium";
  const reduced = useReducedMotion();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [shake, setShake] = useState(false);
  const { submit, status, error, reset, isLoading } = useFormSubmit(
    (data: ContactPayload) => api.submitContact(data)
  );

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updateField = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
    if (shake) setShake(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShake(false);

    const validationErrors = validateContact(form);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setShake(true);
      return;
    }

    setFieldErrors({});
    const ok = await submit({ ...form, source });
    if (ok) {
      setForm({ name: "", email: "", message: "" });
    } else {
      setShake(true);
    }
  };

  const inputClass = premium ? "contact-input" : undefined;

  return (
    <AnimatePresence mode="wait">
      {status === "success" ? (
        <FormSuccessState
          key="success"
          message="Thank you for contacting us!"
        >
          <Button variant="ghost" size="sm" onClick={reset}>
            Send another message
          </Button>
        </FormSuccessState>
      ) : (
        <motion.form
          key="form"
          onSubmit={handleSubmit}
          className={cn(premium ? "space-y-5" : "space-y-3", shake && "animate-shake")}
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? undefined : { opacity: 0 }}
        >
          <MotionField premium={premium}>
            {premium ? (
              <PremiumLabel htmlFor="name">Your Name</PremiumLabel>
            ) : (
              <Label htmlFor="name">Your Name</Label>
            )}
            <Input
              id="name"
              required
              value={form.name}
              onChange={(e) =>
                updateField("name", formatNameInput(e.target.value))
              }
              placeholder={premium ? "John Doe" : "Your Name"}
              className={cn(inputClass, fieldErrors.name && "border-red-500")}
              aria-invalid={!!fieldErrors.name}
              aria-describedby={fieldErrors.name ? "name-error" : undefined}
            />
            {fieldErrors.name && (
              <p
                id="name-error"
                className="mt-1 text-sm text-red-600 dark:text-red-400"
              >
                {fieldErrors.name}
              </p>
            )}
          </MotionField>
          <MotionField premium={premium}>
            {premium ? (
              <PremiumLabel htmlFor="email">Your Email</PremiumLabel>
            ) : (
              <Label htmlFor="email">Your Email</Label>
            )}
            <Input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              placeholder={premium ? "you@example.com" : "your@email.com"}
              className={cn(inputClass, fieldErrors.email && "border-red-500")}
              aria-invalid={!!fieldErrors.email}
              aria-describedby={fieldErrors.email ? "email-error" : undefined}
            />
            {fieldErrors.email && (
              <p
                id="email-error"
                className="mt-1 text-sm text-red-600 dark:text-red-400"
              >
                {fieldErrors.email}
              </p>
            )}
          </MotionField>
          <MotionField premium={premium}>
            {premium ? (
              <PremiumLabel htmlFor="message">Your Message</PremiumLabel>
            ) : (
              <Label htmlFor="message">Your Message</Label>
            )}
            <Textarea
              id="message"
              required
              rows={5}
              value={form.message}
              onChange={(e) => updateField("message", e.target.value)}
              placeholder={premium ? "How can we help you?" : "Your message..."}
              className={cn(inputClass, fieldErrors.message && "border-red-500")}
              aria-invalid={!!fieldErrors.message}
              aria-describedby={
                fieldErrors.message ? "message-error" : undefined
              }
            />
            {fieldErrors.message && (
              <p
                id="message-error"
                className="mt-1 text-sm text-red-600 dark:text-red-400"
              >
                {fieldErrors.message}
              </p>
            )}
          </MotionField>
          {error && (
            <p
              id="contact-form-error"
              role="alert"
              aria-live="polite"
              className="text-sm text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}
          <MotionButton
            type="submit"
            isLoading={isLoading}
            loadingText="Sending..."
            className={cn(
              premium
                ? "contact-btn-primary w-full disabled:opacity-50"
                : "w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white border border-primary shadow-sm hover:bg-primary/90 disabled:opacity-50"
            )}
            disabled={isLoading}
          >
            Send Message
          </MotionButton>
          {premium && (
            <p className="text-center text-xs text-[var(--contact-muted)]">
              Your information is kept private and never shared.
            </p>
          )}
        </motion.form>
      )}
    </AnimatePresence>
  );
}
